import { type CheckboxProps, type PaginationProps } from "@mantine/core";
import { type ChangeEvent, useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useGetApi } from "src/hooks";
import { type ISection } from "src/interfaces";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setUsers } from "src/redux/slices/entriesGeneratorSlice";
import { setFiles } from "src/redux/slices/generatorSlice";
import {
  addToVerify,
  removeFromVerify,
  resetVerifySlice,
  setComments,
} from "src/redux/slices/verifySlice";
import { getYachtId } from "src/utils";
export interface HistoricalSnapshotProps {
  lbid?: string;
  preview?: boolean;
}
export const useVerifyEntries = (
  entryRequest: string,
  listRequest: string,
  body: any,
) => {
  const dispatch = useDispatch();
  const { idType, lbeidOrPartId, aid } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toVerify = useSelector(state => state.verifySlice.toVerify);
  const pendingVerify = useSelector(
    state => state.pendingRequestsSlice.pendingVerify,
  );
  const isPendingVerify = useMemo(
    () =>
      !!pendingVerify.find(p => p[String(idType) as "lbeid"] === lbeidOrPartId),
    [idType, lbeidOrPartId, pendingVerify],
  );

  const mapReviewEntry = useCallback(
    (data: any) => {
      dispatch(setFiles(data.data?.attachments ?? []));
      return {
        sections: data.data.lbentry.sections as ISection[],
        verifiable: data.data.verifiable,
        lbname: data.data.lbname,
        restricted: data.data.restricted,
        authorized: data.data.authorized,
        lbid: data.data.lbid,
        idata: data.data.idata,
        deleted: data.data.deleted,
        status: data.data.status,
        comments: data.data.comments,
      };
    },
    [dispatch],
  );

  const mapList = useCallback(
    (data: any) => {
      dispatch(setUsers(data.data.content.data.users));

      const list = data.data.review.data.map(
        (entry: {
          meid?: string;
          partid?: string;
          lbeid: string;
          weid?: string;
          rstatus: number | null;
        }) => {
          return {
            id: entry.weid ?? entry.meid ?? entry.partid ?? entry.lbeid,
            idType: entry.weid
              ? "weid"
              : entry.meid
              ? "meid"
              : entry.partid
                ? "partid"
                : entry.lbeid
                  ? "lbeid"
                  : "",
            rstatus: entry.rstatus,
          };
        },
      ) as Array<{ id: string; idType: string }>;

      return {
        list,
        expired: data.data.review.audit.expired,
      };
    },

    [dispatch],
  );

  const {
    data,
    refetch: fetchEntry,
    isLoading: isLoading1,
  } = useGetApi({
    request: entryRequest,
    body,
    mapper: mapReviewEntry,
    fetchOnInit: false,
  });

  const {
    data: reviewData,
    refetch: fetchUsers,
    isLoading: isLoading2,
  } = useGetApi({
    request: listRequest,
    body: {
      params: {
        _yachtid: getYachtId(),
        _aid: aid,
      },
    },
    mapper: mapList,
    fetchOnInit: false,
  });

  const setComments = useSetComments();

  useEffect(() => {
    if (data?.comments) {
      setComments(data.comments);
    }
  }, [data?.comments]);
  const fetch = useCallback(async () => {
    await Promise.all([fetchEntry(), fetchUsers()]);
  }, [fetchEntry, fetchUsers]);

  useEffect(() => {
    void fetch();
  }, [fetch]);

  useEffect(() => {
    return () => {
      setComments({ comments: [] });
    };
  }, [dispatch]);

  const handleEntryChange = useCallback(
    (index: number) => {
      if (!reviewData?.list) return;
      const des = reviewData?.list[index - 1];
      if (!des) return;
      navigate(
        replaceTwoLastParameters(location.pathname, des.idType, des.id),
        {
          replace: true,
        },
      );
    },
    [reviewData?.list, navigate, location.pathname],
  );

  const handleCheckbox = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const item = {
        id: lbeidOrPartId as string,
        idType: String(idType),
      };
      if (event.target.checked) {
        dispatch(addToVerify(item));
      } else {
        dispatch(removeFromVerify(item));
      }
    },
    [dispatch, idType, lbeidOrPartId],
  );

  const paginationProps: PaginationProps = useMemo(
    () => ({
      total: reviewData?.list?.length ?? 1,
      value: reviewData?.list
        ? reviewData?.list.findIndex(id => id.id === lbeidOrPartId) + 1
        : 1,
      onChange: handleEntryChange,
    }),
    [handleEntryChange, reviewData?.list, lbeidOrPartId],
  );

  const checkboxProps: CheckboxProps = useMemo(
    () =>
      isPendingVerify
        ? {
            checked: true,
            disabled: true,
          }
        : {
            checked: toVerify.some(e => e.id === lbeidOrPartId),
            onChange: handleCheckbox,
          },
    [handleCheckbox, isPendingVerify, lbeidOrPartId, toVerify],
  );
  const historicalSnapshotProps = useMemo(
    () => ({ lbid: data?.lbid }),
    [data?.lbid],
  );
  return {
    sections: data?.sections ?? [],
    verifiable: data?.verifiable,
    lbname: data?.lbname,
    restricted: data?.restricted,
    authorized: data?.authorized,
    lbid: data?.lbid,
    idata: data?.idata,
    deleted: data?.deleted,
    status: data?.status,
    preview: true,
    expired: reviewData?.expired,
    isLoading: isLoading1 || isLoading2,

    paginationProps,
    checkboxProps,
    historicalSnapshotProps,
  };
};

interface Reverify {
  client: string;
  date: string;
  fname: string;
  message: string;
  role: string;
  sname: string;
  userid: string;
}

interface Comment {
  date: string;
  from: string;
  message: string;
}

interface ParentCommentObject {
  audit: any;
  comments: Comment[];
  reverify: Reverify[];
}

export type CommentObject = string | Comment[] | ParentCommentObject;

export const useSetComments = () => {
  const dispatch = useDispatch();
  const handleSetComments = (comments: any) => {
    let formattedComments;
    if (comments?.reverify) {
      formattedComments = comments.reverify;
    } else {
      formattedComments = comments;
    }
    if (typeof comments === "string") {
      formattedComments = JSON.parse(comments);
    }
    if (formattedComments?.comments) {
      formattedComments = comments.comments;
    }
    dispatch(setComments(formattedComments));
  };

  return handleSetComments;
};

const replaceTwoLastParameters = (
  url: string,
  newFirstParameter: any,
  newSecondParameter: string,
) => {
  const urlParts = url.split(`/`);
  urlParts[urlParts.length - 2] = newFirstParameter;
  urlParts[urlParts.length - 1] = newSecondParameter;

  return urlParts.join(`/`);
};
